import { memo, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { gql, NetworkStatus, useQuery } from '@apollo/client';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import {
  Query,
  QuerySchedulesArgs,
  Schedule,
  ScheduleConnection,
} from '@lgg/isomorphic/types/__generated__/graphql';
import {
  LggDropdownButtonWithoutOverlay,
  LggSelectableOptionsDropdownButton,
} from 'src/components/general/button/dropdown-button';
import { ExportResultsButton } from 'src/components/general/button/export-results-button';
import { FiltersButton } from 'src/components/general/button/filters-button';
import { GraphqlTablePagination } from 'src/components/general/display/graphql-table-pagination';
import { InAppNotification } from 'src/components/general/display/in-app-notification';
import { PaginationInfo } from 'src/components/general/display/pagination-info';
import { SelectableOptionsDrawer } from 'src/components/general/drawer/bottom/selectable-options-bottom-drawer';
import {
  TableBodyPlaceholder,
  TableLayoutPlaceholder,
} from 'src/components/general/feedback/loading-placeholders';
import {
  BasePageQueryParams,
  getSortDirectionByOrder,
  SorterData,
  TableLayoutFiltersContainer,
  TableLayoutHeader,
  tableLayoutViewButtonStyles,
  TableSortData,
} from 'src/components/general/table-helpers';
import { FlexRow } from 'src/components/layout/flex-row';
import { TableLayoutPageContainer } from 'src/components/layout/table-layout-page-container';
import { PriorityOptionsDrawerStyle } from 'src/components/pages/appointments/components/appointment-priority-picker';
import {
  AppointmentsFilters,
  AppointmentsFiltersFormValues,
  defaultAppointmentsFilters,
} from 'src/components/pages/appointments/components/appointments-filters';
import { AppointmentsTable } from 'src/components/pages/appointments/components/appointments-table';
import {
  useAppointmentsStatusOptions,
  useAppointmentsWhereInputResolver,
} from 'src/components/pages/appointments/hooks';
import { CORE_PAGE_INFO_FIELDS } from 'src/components/providers/apollo-provider-provider';
import { useRefreshAppointment } from 'src/hooks/gql/use-refresh-appointment';
import { UseRefreshProps } from 'src/hooks/gql/use-refresh.shared';
import { useAddBreadcrumb } from 'src/hooks/use-add-breadcrumb';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useDateHelpers } from 'src/hooks/use-date-helpers';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useInstitutionUrl } from 'src/hooks/use-institution-url';
import {
  useFormatLegacyDateQueryString,
  useLegacyParamsForDashboardQuery,
  usePatchRawQueryParams,
  viewCodeQueryParamKey,
} from 'src/hooks/use-legacy-params-for-dashboard-query';
import { usePushUrlWithViewCode } from 'src/hooks/use-push-url-with-view-code';
import { useVisible } from 'src/hooks/use-visible';
import { getNodesFromConnection } from 'src/utils/graphql/get-nodes-from-connection';
import { parseQueryParams } from 'src/utils/parse-query-params';

const GET_APPOINTMENTS_QUERY = gql`
  ${CORE_PAGE_INFO_FIELDS}
  query GetSchedules(
    $institutionId: Int!
    $first: Int
    $after: String
    $before: String
    $last: Int
    $where: ScheduleWhereInput
    $orderBy: ScheduleOrderByInput
  ) {
    schedules(
      institutionId: $institutionId
      first: $first
      after: $after
      before: $before
      last: $last
      where: $where
      orderBy: $orderBy
    ) {
      totalCount
      pageInfo {
        ...PageInfoFragment
      }
      edges {
        node {
          id
          title
          priority
          status
          startAt
          isAllDay
          contact {
            id
            label
            interest
            primaryEmail
            primaryPhone {
              national
              e164
              regionCode
            }
            secondaryPhone {
              national
              e164
              regionCode
            }
            tags {
              id
              name
              isActive
            }
            status {
              id
              name
            }
            stage {
              id
              name
              slug
            }
          }
          user {
            id
            fullName
            avatar {
              url(transform: { width: 180, height: 180 })
              initials
              color
            }
            role {
              id
              name
            }
          }
        }
      }
    }
    overdueAppointmentsCount: schedules(
      institutionId: $institutionId
      where: {
        status: {
          _in: [RESCHEDULED, SCHEDULED]
        },
        startAt: { _lte: "${new Date().toISOString()}" }
      }
    ) {
      totalCount
    }
  }
`;

type AppointmentsPageAdvancedQueryParams = Omit<
  AppointmentsFiltersFormValues,
  'agent' | 'group'
> & {
  agent?: string;
  group?: string;
  created_by?: string;
};

type AppointmentsPageSortQueryParams = {
  table?: string;
};

export type AppointmentsPageQueryParams = BasePageQueryParams<
  'upcoming' | 'past' | 'overdue' | 'canceled' | 'no-show' | 'today',
  AppointmentsPageAdvancedQueryParams
>;

enum AppointmentsViewCodeFilter {
  Upcoming = 'upcoming',
  Past = 'past',
  Overdue = 'overdue',
  Canceled = 'canceled',
  No_Show = 'no-show',
  Custom = 'custom',
  Today = 'today',
}

const APPOINTMENTS_QUERY_PAGE_SIZE = 50;

const HeaderInnerContainer = styled(FlexRow)`
  & > *:not(:last-child) {
    margin-right: 10px;

    ${up('md')} {
      margin-right: 20px;
    }
  }
`;

const DesktopViewButton = styled(LggSelectableOptionsDropdownButton)`
  ${tableLayoutViewButtonStyles}
`;

const MobileViewButton = styled(LggDropdownButtonWithoutOverlay)`
  ${tableLayoutViewButtonStyles}
`;

const filtersStateResolver = (
  queryParams: AppointmentsPageQueryParams,
): Partial<AppointmentsFiltersFormValues> | undefined => {
  if (!queryParams.q?.advanced) {
    return undefined;
  }

  const { advanced } = queryParams.q;

  const values: Partial<AppointmentsFiltersFormValues> = {};

  if (advanced.date_from) {
    values.date_from = advanced.date_from;
  }

  if (advanced.date_to) {
    values.date_to = advanced.date_to;
  }

  if (advanced.title) {
    values.title = advanced.title;
  }

  if (advanced.status) {
    values.status = advanced.status;
  }

  if (advanced.priority) {
    values.priority = advanced.priority;
  }

  if (advanced.agent) {
    values.agent = parseInt(advanced.agent);
  }

  if (advanced.group) {
    values.group = parseInt(advanced.group);
  }

  if (advanced.created_by) {
    values.created_by = parseInt(advanced.created_by);
  }

  return values;
};

const sortInputResolver = (sortData: TableSortData) => {
  const { key, direction = 'ASC' } = sortData;

  switch (key) {
    case 'contact': {
      return {
        contact: {
          firstName: direction,
        },
      };
    }
    case 'user': {
      return {
        user: {
          firstName: direction,
        },
      };
    }
    default: {
      return {
        [key]: direction,
      };
    }
  }
};

const useViewCodeResolver = (viewCode: AppointmentsPageQueryParams['view-code']) => {
  const { startOfDay, endOfDay, initDayJsDate } = useDateHelpers();
  const { formatLegacyDateQueryString } = useFormatLegacyDateQueryString();

  const nowDate = useMemo(
    () => initDayJsDate(new Date()).toDate(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewCode],
  );
  const now = useMemo(
    () => formatLegacyDateQueryString(nowDate),
    [formatLegacyDateQueryString, nowDate],
  );

  return (rawQueryParams: AppointmentsPageQueryParams): AppointmentsPageQueryParams => {
    const viewCode = rawQueryParams[viewCodeQueryParamKey];
    let advanceParams: AppointmentsPageAdvancedQueryParams = {};
    const sortingParams: AppointmentsPageSortQueryParams = {};

    switch (viewCode) {
      case 'today': {
        advanceParams = {
          date_from: formatLegacyDateQueryString(startOfDay(nowDate)),
          date_to: formatLegacyDateQueryString(endOfDay(nowDate)),
        };
        sortingParams.table = 'sort|startAt|direction|ASC';
        break;
      }
      case 'upcoming': {
        advanceParams = {
          status: ['RESCHEDULED', 'SCHEDULED'],
          date_from: now,
        };
        sortingParams.table = 'sort|startAt|direction|ASC';
        break;
      }
      case 'past':
        advanceParams = {
          date_to: now,
        };
        sortingParams.table = 'sort|startAt|direction|DESC';

        break;
      case 'overdue': {
        advanceParams = {
          status: ['RESCHEDULED', 'SCHEDULED'],
          date_to: now,
        };
        sortingParams.table = 'sort|startAt|direction|DESC';
        break;
      }
      case 'canceled': {
        advanceParams = {
          status: ['CANCELLED'],
        };
        sortingParams.table = 'sort|startAt|direction|DESC';
        break;
      }
      case 'no-show': {
        advanceParams = {
          status: ['NO-SHOW'],
        };
        sortingParams.table = 'sort|startAt|direction|DESC';
        break;
      }
      case 'custom':
        break;
    }

    const params = {
      ...sortingParams,
      ...rawQueryParams,
    };

    if (Object.keys(advanceParams)) {
      return {
        ...params,
        q: {
          advanced: advanceParams,
        },
      };
    } else {
      return params;
    }
  };
};

export const AppointmentsPage = memo(() => {
  const { t } = useTranslation(['appointments', 'common']);
  useAddBreadcrumb(t('appointments:pageBreadcrumb'));
  const { id: institutionId } = useCurrentInstitution();
  const institutionUrl = useInstitutionUrl();
  const handleGraphQLError = useHandleGraphQLError();
  const rawQueryParams = usePatchRawQueryParams<
    AppointmentsPageAdvancedQueryParams,
    AppointmentsPageQueryParams['view-code']
  >({ defaultViewCode: 'today' });
  const viewCode = rawQueryParams['view-code'];
  const viewCodeResolver = useViewCodeResolver(viewCode);
  const breakpointUpMd = useBreakpoint(up('md'));
  const viewCodeDropdownVisibilityHandler = useVisible();
  const filtersVisibilityHandler = useVisible();
  const location = useLocation();

  const { pushUrlWithViewCode } =
    usePushUrlWithViewCode<AppointmentsPageQueryParams['view-code']>();
  const whereInputResolver = useAppointmentsWhereInputResolver(viewCode);
  const appointmentStatusOptions = useAppointmentsStatusOptions({ includeIcons: false });
  const allAppointmentStatuses = useMemo(
    () => appointmentStatusOptions.map((option) => option.value),
    [appointmentStatusOptions],
  );

  // Legacy defaults to a subset of statuses when none is provided, while the
  // app shows all of them. To keep the exported/legacy URL in sync with the UI,
  // explicitly send every status when the params don't include one.
  const legacyFiltersQueryParamsResolver = useCallback(
    (queryParams: AppointmentsPageQueryParams): AppointmentsPageQueryParams => {
      const currentStatus = queryParams.q?.advanced?.status;

      if (currentStatus && currentStatus.length > 0) {
        return queryParams;
      }

      return {
        ...queryParams,
        q: {
          ...queryParams.q,
          advanced: {
            ...queryParams.q?.advanced,
            status: allAppointmentStatuses,
          },
        },
      };
    },
    [allAppointmentStatuses],
  );

  const {
    variables,
    sortData,
    hasActiveFilters,
    handleSortChange,
    handleNextPage,
    handlePreviousPage,
    filters,
    legacyFiltersQueryString,
  } = useLegacyParamsForDashboardQuery({
    pageSize: APPOINTMENTS_QUERY_PAGE_SIZE,
    defaultFilters: defaultAppointmentsFilters,
    dateFilterKeys: ['date_from', 'date_to'],
    defaultSortData: {
      key: 'startAt',
      direction: 'ASC',
    },
    rawQueryParams,
    viewCodeResolver,
    whereInputResolver,
    sortInputResolver,
    filtersStateResolver,
    legacyFiltersQueryParamsResolver,
  });

  const {
    data: appointmentsData,
    loading: loadingAppointments,
    refetch: refetchAppointmentsData,
    error,
    networkStatus,
  } = useQuery<
    Pick<Query, 'schedules'> & { overdueAppointmentsCount: ScheduleConnection },
    Partial<QuerySchedulesArgs>
  >(GET_APPOINTMENTS_QUERY, {
    variables: {
      ...variables,
      institutionId,
    },
    onError: handleGraphQLError,
  });

  useEffect(() => {
    // This effect sets the sort order for the appointments in the url based on view-code
    // This keeps the sort order when filtering the list, since the view-code will change to "custom"
    const search = location.search;
    const queryParams = parseQueryParams(search);
    const sortParam = queryParams['table'];

    if (!sortParam) {
      const view = queryParams['view-code'];
      const sortDirection =
        Boolean(view) && view !== AppointmentsViewCodeFilter.Upcoming ? 'DESC' : 'ASC';

      handleSortChange({ columnKey: 'startAt', sortDirection });
    }
  }, [handleSortChange, location.search]);

  const appointmentsIds = getNodesFromConnection(appointmentsData?.schedules).map(
    (node) => node.id,
  );

  const onRefreshHandler = useMemo<UseRefreshProps>(() => {
    return {
      onRefresh: async (params) => {
        const { id, action } = params;

        if (action === 'create' || appointmentsIds?.includes(id)) {
          await refetchAppointmentsData();
        }
      },
    };
  }, [appointmentsIds, refetchAppointmentsData]);

  useRefreshAppointment(onRefreshHandler);

  const overdueAppointmentsCount =
    appointmentsData?.overdueAppointmentsCount.totalCount ?? 0;

  const selectedViewCodeDescription = useMemo(() => {
    let text: string;

    switch (viewCode) {
      case 'today': {
        text = t('common:pointInTime.today');
        break;
      }
      case 'upcoming': {
        text = t('appointments:viewFilter.options.upcoming');
        break;
      }
      case 'past': {
        text = t('appointments:viewFilter.options.past');
        break;
      }
      case 'overdue': {
        text = t('appointments:viewFilter.options.overdue');
        break;
      }
      case 'no-show': {
        text = t('appointments:viewFilter.options.noShow');
        break;
      }
      case 'canceled': {
        text = t('appointments:viewFilter.options.canceled');
        break;
      }
      case 'custom': {
        text = t('common:custom');
        break;
      }
    }

    return text;
  }, [t, viewCode]);

  const isFetchingMore = networkStatus === NetworkStatus.setVariables;
  const isRefetching = networkStatus === NetworkStatus.refetch;

  if (loadingAppointments && !appointmentsData && !isRefetching) {
    return (
      <TableLayoutPageContainer>
        <TableLayoutPlaceholder />
      </TableLayoutPageContainer>
    );
  }

  if (error) return <div>error</div>;
  if (!appointmentsData) return null;

  const {
    schedules: { totalCount, pageInfo },
  } = appointmentsData;
  const appointments = getNodesFromConnection(appointmentsData.schedules);

  const applyOverdueFilter = () => {
    pushUrlWithViewCode(AppointmentsViewCodeFilter.Overdue);
  };

  const viewOptions = [
    {
      label: t('common:pointInTime.today'),
      'data-lgg-id': 'appointments-page-view-button-option-today',
      value: AppointmentsViewCodeFilter.Today,
      onClick: () => {
        pushUrlWithViewCode(AppointmentsViewCodeFilter.Today);
      },
    },
    {
      label: t('appointments:viewFilter.options.upcoming'),
      'data-lgg-id': 'appointments-page-view-button-option-upcoming',
      value: AppointmentsViewCodeFilter.Upcoming,
      onClick: () => {
        pushUrlWithViewCode(AppointmentsViewCodeFilter.Upcoming);
      },
    },
    {
      label: t('appointments:viewFilter.options.past'),
      'data-lgg-id': 'appointments-page-view-button-option-past',
      value: AppointmentsViewCodeFilter.Past,
      onClick: () => {
        pushUrlWithViewCode(AppointmentsViewCodeFilter.Past);
      },
    },
    {
      label: t('appointments:viewFilter.options.overdue'),
      'data-lgg-id': 'appointments-page-view-button-option-overdue',
      value: AppointmentsViewCodeFilter.Overdue,
      onClick: applyOverdueFilter,
    },
    {
      label: t('appointments:viewFilter.options.canceled'),
      'data-lgg-id': 'appointments-page-view-button-option-canceled',
      value: AppointmentsViewCodeFilter.Canceled,
      onClick: () => {
        pushUrlWithViewCode(AppointmentsViewCodeFilter.Canceled);
      },
    },
    {
      label: t('appointments:viewFilter.options.noShow'),
      'data-lgg-id': 'appointments-page-view-button-option-no-show',
      value: AppointmentsViewCodeFilter.No_Show,
      onClick: () => {
        pushUrlWithViewCode(AppointmentsViewCodeFilter.No_Show);
      },
    },
  ];

  const hasNotifications = overdueAppointmentsCount > 0;
  const appointmentNotification =
    viewCode !== AppointmentsViewCodeFilter.Overdue && hasNotifications ? (
      <InAppNotification
        icon="schedule18"
        testId="appointments-page-overdue-notification"
        onClick={applyOverdueFilter}
        title={t(
          `appointments:overdueNotification.${breakpointUpMd ? 'titleShort' : 'title'}`,
          {
            count: overdueAppointmentsCount,
            appointmentsCount: overdueAppointmentsCount.toLocaleString(),
          },
        )}
        variant="error"
      />
    ) : null;

  const viewButton = breakpointUpMd ? (
    <DesktopViewButton
      visibilityHandler={viewCodeDropdownVisibilityHandler}
      data-lgg-id="appointments-page-view-button"
      selectedValue={viewCode}
      customDropdownProps={{
        overlayStyle: {
          minWidth: '141px',
        },
      }}
      options={viewOptions}
      variant="defaultWhite"
      size="regular"
    >
      {selectedViewCodeDescription}
    </DesktopViewButton>
  ) : (
    <>
      <MobileViewButton
        data-lgg-id="appointments-page-view-button"
        variant="defaultWhite"
        size="regular"
        onClick={() =>
          viewCodeDropdownVisibilityHandler.setVisible(
            !viewCodeDropdownVisibilityHandler.visible,
          )
        }
        isActive={viewCodeDropdownVisibilityHandler.visible}
      >
        {selectedViewCodeDescription}
      </MobileViewButton>
      <SelectableOptionsDrawer
        title={selectedViewCodeDescription}
        options={viewOptions}
        selectedValue={viewCode}
        onClose={viewCodeDropdownVisibilityHandler.close}
        visible={viewCodeDropdownVisibilityHandler.visible}
      />
    </>
  );

  return (
    <TableLayoutPageContainer data-lgg-id="appointments-page-container">
      <TableLayoutHeader
        leftContent={
          <HeaderInnerContainer>
            {viewButton}
            {breakpointUpMd && appointmentNotification}
          </HeaderInnerContainer>
        }
        rightContent={
          <TableLayoutFiltersContainer>
            <FiltersButton
              filtersVisibilityHandler={filtersVisibilityHandler}
              hasActiveFilters={hasActiveFilters}
              data-lgg-id="appointments-page-filters-button"
            />
            <ExportResultsButton
              testId="appointments-page-export"
              requestUrl={`${institutionUrl}cms/appointments/`}
              params={legacyFiltersQueryString}
              hideShareOption
            />
          </TableLayoutFiltersContainer>
        }
        bottomContent={
          <PaginationInfo
            total={totalCount}
            entityName={t('appointments:appointment', {
              count: totalCount,
            })}
            testId="appointments-page-pagination"
          />
        }
      />
      {isFetchingMore ? (
        <TableBodyPlaceholder />
      ) : (
        <>
          {!breakpointUpMd && appointmentNotification}
          <AppointmentsTable
            appointments={appointments}
            refetchQueriesHandler={async () => {
              await refetchAppointmentsData();
            }}
            sortData={sortData}
            onChange={(pagination, filters, sorter) => {
              const { columnKey, order } = sorter as SorterData<Schedule>;
              const sortDirection = getSortDirectionByOrder(order);
              handleSortChange({ columnKey, sortDirection });
            }}
          />
          <PriorityOptionsDrawerStyle />
          <GraphqlTablePagination
            previousPageHandler={handlePreviousPage}
            nextPageHandler={handleNextPage}
            pageInfo={pageInfo}
          />
          <AppointmentsFilters
            visible={filtersVisibilityHandler.visible}
            onClose={filtersVisibilityHandler.close}
            filters={filters}
          />
        </>
      )}
    </TableLayoutPageContainer>
  );
});
